import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, capitalize } from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled, useTheme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// import { capitalize } from '@mui/material';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import TableContainer from "@mui/material/TableContainer";
import { getComparator, stableSort } from "../utils/systemInfo.js";

import SearchInput from "../common/searchInput.js";
import DevicesService from "../api/devices.js";
// import HomePagesMenu from '../common/HomePagesMenu';
import Loading from "../common/loading.js";
import EnhancedTableHead from "../common/EnhancedTableHead.js";
import Breadcrumbs from "../common/breadcrumbs.js";
import exportTableToCSV from "../common/customFunctions/exportTableToCSV.js";
import StyledTypography from "../common/StyledTypography.js";
import handleNavigate from "../common/customFunctions/handleNavigate.js";
import { setMachineGroupsData } from "../store/machineGroupsSlice.js";
import MachineGroupsTableRow from "./machineGroupsTableRow.js";
import { setDeviceGroup } from "../store/deviceGroupSlice.js";

const StyledTablePagination = styled((props) => <TablePagination {...props} />)(
  ({ theme }) => ({
    color: theme.palette.custom.text,
    "& .MuiTablePagination-selectIcon": {
      color: theme.palette.custom.text,
    },
  })
);
const StyledButton = styled((props) => <Button {...props} />)(({ theme }) => ({
  minWidth: "fit-content",
  width: "150px",
  height: "32px",
  margin: "0px",
  color: theme.palette.custom.buttonTextColor,
  backgroundColor: theme.palette.custom.buttonColor,
  borderColor: theme.palette.custom.borderColor,
  borderWidth: "1px",
  borderStyle: "solid",
  "&:hover": {
    backgroundColor: theme.palette.custom.buttonHoverColor,
    color: theme.palette.custom.buttonHoverTextColor,
    boxShadow: "none",
  },
}));
const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Name",
    sort: true,
  },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: "Description",
    sort: true,
  },
  {
    id: "physical_address",
    numeric: false,
    disablePadding: false,
    label: "Physical address",
    sort: true,
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    sort: true,
  },
  {
    id: "updated",
    numeric: false,
    disablePadding: false,
    label: "Updated",
    sort: true,
  },
  {
    id: "view",
    numeric: false,
    disablePadding: false,
    label: "View",
    sort: false,
  },
];
function MachineGroups() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const organziation = useSelector(({ organziation }) => organziation);
  const machineGroups = useSelector(({ machineGroups }) => machineGroups);
  const user = useSelector(({ user }) => user);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("DeviceLastSeen");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(localStorage.getItem("rowsPerPage")) ?? 5
  );
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState([]);
  const [loadingValue, setLoadingValue] = useState(true);
  const pageName =
    window.location.href.split("/")[window.location.href.split("/").length - 1];
  const breadcrumbs = [
    { id: 0, name: "Home", href: "./" },
    { id: 1, name: capitalize(pageName), href: pageName },
  ];
  useEffect(() => {
    const orgObj =
      organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
    const apiKey = orgObj?.api_key_list?.[0]?.key;
    DevicesService.getDeviceGroups({
      operation_uid: orgObj?.uid,
      person_id: pageName?.toLowerCase()?.includes("my") ? user.profileId : "",
      apiKey,
    })
      .then((json) => {
        if (json && json.person_device_group) {
          dispatch(
            setMachineGroupsData({
              json: json.person_device_group,
            })
          );
        }
        setLoadingValue(false);
      })
      .catch((errorr) => {
        if (process.env.NODE_ENV === "development")
          console.log("error", errorr);
      });
    return () => {
      setLoadingValue(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    localStorage.setItem("rowsPerPage", event.target.value);
    setPage(0);
  };
  const handleClick = (event, row) => {
    const selectedIndex = selected.indexOf(row.DeviceID);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row.DeviceID);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };
  function handleView(event, row) {
    dispatch(setDeviceGroup(row));
    handleNavigate(event, navigate, `/${pageName}/viewDeviceGroup`);
  }
  function handleEdit(event, row) {
    dispatch(setDeviceGroup(row));
    handleNavigate(event, navigate, `/${pageName}/addEditDeviceGroup`);
  }
  function handleSearch(ev) {
    setPage(0);
    setLoadingValue(true);
    const newSearchText = ev.target.value;
    setSearchText(newSearchText);
    let machineGroupsData = { machineGroups };
    machineGroupsData.machineTypeFilter = "";
    machineGroupsData.newSearchText = newSearchText;
    dispatch(setMachineGroupsData(machineGroupsData));
    setLoadingValue(false);
  }
  const visibleRows = useMemo(() => {
    if (machineGroups && machineGroups.length > 0) {
      return stableSort(machineGroups, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
    } else {
      return [];
    }
  }, [order, orderBy, page, rowsPerPage, machineGroups]);
  function handleAddDeviceGroup(event) {
    const deviceGroup = {
      person_device_group_id: "",
      operation_uid: organziation?.uid,
      person_id: "",
      name: "",
      description: "",
      physical_address: "",
      status: "active",
    };
    if (pageName?.toLowerCase()?.includes("my")) {
      deviceGroup.person_id = user.profileId;
    }
    dispatch(setDeviceGroup(deviceGroup));
    handleNavigate(event, navigate, `addEditDeviceGroup`); // be removing the '/' from the beginning we don't need the pageName ${pageName}
  }
  const isSelected = (name) => selected.indexOf(name) !== -1;
  return (
    <Box
      sx={{
        p: "12px",
        minHeight: "90vh",
        height: "fit-content",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "block", sm: "center" },
          flexWrap: "wrap"
        }}
      >
        <Box sx={{ display: { xs: "block", sm: "flex" } }}>
          <Box sx={{ width: "100%" }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Typography
              sx={{
                fontSize: "45px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              {pageName?.toLocaleLowerCase()?.includes("device")
                ? t("DeviceGroups")
                : t("MachineGroups")}
            </Typography>
          </Box>
        </Box>
        <StyledButton variant="contained" onClick={handleAddDeviceGroup}>
          {t("Add Group")}
        </StyledButton>
      </Box>
      <Divider
        sx={{ my: "10px", backgroundColor: theme.palette.custom.borderColor }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <SearchInput searchText={searchText} handleSearch={handleSearch} />
      </Box>
      {loadingValue ? (
        <Box sx={{ height: "30vh", pt: "10%" }}>
          <Loading />
        </Box>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              headCells={headCells.filter(
                (hc) =>
                  hc.id !== "NamedLocationOriginal" ||
                  visibleRows.find(
                    (vr) =>
                      vr.NamedLocationOriginal &&
                      vr.NamedLocationOriginal !== "Unknown"
                  )
              )}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />

            <TableBody>
              {machineGroups &&
              machineGroups.length > 0 &&
              visibleRows.length ? (
                visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.DeviceID);
                  return (
                    <MachineGroupsTableRow
                      key={index}
                      row={row}
                      handleClick={handleClick}
                      isItemSelected={isItemSelected}
                      handleView={handleView}
                      handleEdit={handleEdit}
                    />
                  );
                })
              ) : (
                <StyledTypography>{t("No entries found")}</StyledTypography>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <StyledTablePagination
        SelectProps={{
          inputProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: theme.palette.secondary.dark,
                  color: theme.palette.custom.text,
                  "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: theme.palette.custom.borderColor,
                  },
                  "& .MuiMenuItem-root:hover": {
                    backgroundColor: "blue",
                  },
                  "& .MuiMenuItem-root.Mui-selected:hover": {
                    backgroundColor: "blue",
                  },
                },
              },
            },
          },
        }}
        rowsPerPageOptions={[5, 10, 15, 20, 25, 50, 100]}
        component="div"
        count={machineGroups ? machineGroups.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Button
        variant="contained"
        sx={{
          backgroundColor: theme.palette.custom.borderColor,
          color: "white",
          "&:hover": {
            backgroundColor: theme.palette.custom.buttonHoverColor,
            color: theme.palette.custom.buttonHoverTextColor,
            boxShadow: "none",
          },
          float: "right",
          width: "min-content",
          whiteSpace: "nowrap",
          padding: "1rem",
        }}
        onClick={() =>
          exportTableToCSV(
            headCells.filter(
              (hc) =>
                hc.id !== "NamedLocationOriginal" ||
                visibleRows.find(
                  (vr) =>
                    vr.NamedLocationOriginal &&
                    vr.NamedLocationOriginal !== "Unknown"
                )
            ),
            visibleRows,
            "Machines Groups List"
          )
        }
      >
        {t("Download List (*.csv)")}
      </Button>
    </Box>
  );
}

export default MachineGroups;
